/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { RoleType } from '@tock/coretypes';
import _ from 'lodash';
import _noop from 'lodash/noop';

import { ActiveAuth, logger } from '@tock/logging';
import { amplitudeSendEvent, setDimensions } from './analytics/amplitude';
import AppSettings from 'settings/AppSettings';

export interface ITrack {
  category: string;
  action?: string;
  label?: string;
  value?: number;
}

let sendEvent: (
  eventCategory: string,
  eventAction: string,
  eventLabel?: string | null,
  eventValue?: string | null,
  fieldsObject?: object | null
) => void = _noop;
let track: (track: ITrack) => void = _noop;
let setActiveAuthCustomDimensions = _noop;
let transitionToPage = _noop;
let fbqTrack: (name: string) => void = _noop;

const gaDefault = (...args: any[]) => {
  logger.debug('Analytics.gaTrack', ...args);
};

const fbqDefault = (...args: any[]) => {
  logger.debug('Analytics.fbqTrack', ...args);
};

const getGa = (): any => {
  if (window.ga) {
    return window.ga;
  }
  return gaDefault;
};

const getFbqTrack = (): any => {
  if (window.fbq) {
    return window.fbq;
  }
  return fbqDefault;
};

if (AppSettings.FACEBOOK_PIXEL_ID) {
  fbqTrack = (name: string) => {
    if (!window.fbq) {
      return;
    }
    getFbqTrack()('track', name);
  };
} else if (location.hostname !== 'localhost') {
  logger.debug('No FACEBOOK_PIXEL_ID was configured, disabling Facebook Pixel.');
}

if (AppSettings.GA_PROPERTY_ID !== null && AppSettings.GA_PROPERTY_ID !== undefined) {
  // GA standard snippet moved to html for easier testing
  // GA initialization performed in ConsentRestrictedScripts -> GoogleAnalytics.tsx

  sendEvent = function (
    eventCategory,
    eventAction,
    eventLabel = null,
    eventValue = null,
    fieldsObject = null
  ) {
    if (!eventCategory || !eventAction) {
      logger.error(
        'sendEvent requires an eventCategory and an eventAction (at least two parameters)'
      );
    }
    getGa()('send', 'event', eventCategory, eventAction, eventLabel, eventValue, fieldsObject);
  };

  track = function track({ category, action, label, value }: ITrack): void {
    if (!category || !action) {
      logger.error('sendEvent requires an category and an action (at least two parameters)');
    }

    getGa()('send', {
      hitType: 'event',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    });
  };

  window.gaTrack = (...params: any[]) => {
    try {
      getGa()(params);
    } catch (e) {
      logger.error(
        'Error updating Google analytics, maybe GA is broken or consent does not include "Functional"',
        e
      );
    }
  };

  /**
   * Set custom dimensions for the current user.  All dimensions must be
   * configured in Google Analytics and values must be strings.
   *
   * @see https://support.google.com/analytics/answer/2709828?hl=en
   */
  setActiveAuthCustomDimensions = function ({
    businessId,
    businessGroupId,
    roleType,
  }: Partial<ActiveAuth>) {
    if (_.isNumber(businessId)) {
      getGa()('set', 'dimension1', `${businessId}`);
    }

    if (_.isNumber(businessGroupId)) {
      getGa()('set', 'dimension2', `${businessGroupId}`);
    }

    if (_.isNumber(roleType)) {
      getGa()('set', 'dimension3', RoleType[roleType]);
    }
  };

  /**
   * Function to call upon routing to a new page.
   *
   * @ param {string} path The path that is being transitioned to
   */
  transitionToPage = function (path) {
    getGa()('set', 'page', path);
    getGa()('send', 'pageview');
  };
} else if (location.hostname !== 'localhost') {
  // eslint-disable-next-line no-console
  console.warn('No GA_PROPERTY_ID was configured, disabling Google Analytics.');
}

export default {
  track,
  /**
   * Send an event to google analytics. Use this method when you need to set a value
   * on the fields object.
   *
   * The track() method is preferred for most use cases
   *
   * @see [ga method reference](https://developers.google.com/analytics/devguides/collection/analyticsjs/command-queue-reference#send)
   **/
  sendEvent,
  sendEventAmplitude: amplitudeSendEvent,
  setActiveAuthCustomDimensions,
  setActiveAuthCustomDimensionsAmplitude: setDimensions,
  transitionToPage,
  fbqTrack,
};
